import {
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  keepPreviousData,
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import compact from 'lodash/compact';
import map from 'lodash/map';
import isNil from 'lodash/isNil';
import {
  Accept,
  useDropzone,
} from 'react-dropzone';
import {
  BillingMultiMediaResponseDTO,
  BillingMultiMediaUploadDTO,
  MultiMediaResponse,
  ClaimBundleDTO,
  MultiMediaResponseDTO,
  EOBWithInsurancePaymentRequestDTO,
  EOBWithInsurancePaymentResponseDTO,
  SplitClaimsDTO,
  CreateSecondaryClaimDTO,
  ClaimAdjustmentReasonCodeDTO,
  RemittanceAdviceRemarkCodeDTO,
  ChargeMasterDTO,
  CustomChargeMasterDTO,
  None,
  PatientStatementRequestDTO,
  SettingsDTO,
  ClaimSettingsDTO,
  EOBDataForClaimViewDTO,
  PatientInvoiceRequestDTO,
  MarkClaimStatusDTO,
  PatientStatementReminderResponseDTO,
  BulkPatientStatementGenerationDTO,
  OnlinePaymentRequestDTO,
  CommunicationResult,
  AppointmentClaimsDTO,
  DeleteRequestDTO,
  ClaimWiseTagRequestDTO,
  BulkPaperClaimsGenerationDTO,
  BulkPaperClaimsTriggerResponseDTO,
  BillingProviderRulesRequestDTO,
  BillingProviderRulesResponseDTO,
  BillingCodeDTO,
  VisitNoteDTO,
  VisitNoteBillDTO,
  ChargeSummaryBundleDTO,
  EOBSummariesDTO,
  DeletePatientPaymentResponseDTO,
  PatientFinalLetterRequestDTO,
  MarkClaimStatusBulkDTO,
  MarkClaimStatusBulkResponseDTO,
  LeanClaimSummaryDTO,
  EncounterPatientWriteOffRequestDTO,
  EncounterPatientWriteOffDTO,
  CreateInsuranceWorkflowResolutionDTO,
  InsuranceWorkflowResolutionDTO,
  ClaimLogViewDTO,
} from 'dtos';
import {
  MultimediaType,
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';
import usePrefetchedData from 'pages/Dashboard/hooks/usePrefetchedData';
import {
  getMultiMedia,
} from 'pages/Dashboard/services/api';
import {
  ClaimSplitsBundle,
  EOBClaimRequest,
  ExcludeServiceLineRequest,
  PatientInvoice,
  PatientStatementRequest,
  Price,
} from 'pages/Dashboard/pages/Billing/types';
import {
  normalizeClaimSplits,
  normalizePatientInvoice,
  paymentNotificationsByVariant,
  serializeEOBWithInsurancePaymentRequest,
  serializePrice,
  serializePatientStatementPayload,
} from 'pages/Dashboard/pages/Billing/helpers';
import {
  useFilterQuery,
  useNotify,
} from 'core/hooks';
import {
  serializeDateRange,
} from 'pages/Dashboard/transport/dateRange';
import {
  DateRange,
} from 'types/dateRange';
import {
  deleteApiChargeDeleteChargePolicy,
  deleteApiChargeDeleteCustomCodeChargePolicy,
  getApiClaimSearchAdjustmentReasonCode,
  getApiClaimSearchRemittanceAdviceRemarkCodes,
  postApiBillingPrintStatementPdf,
  postApiBillingSendStatementPdf,
  getApiSettingsGetWriteOffSettings,
  postApiChargeCreateChargePolicy,
  postApiChargeCreateCustomCodeChargePolicy,
  postApiChargeModifyChargePolicy,
  postApiChargeModifyCustomCodeChargePolicy,
  getApiBillingGetEOBByPatientPatientId,
  getApiBillingGetEOB,
  postApiBillingGetPatientStatement,
  postApiBillingPrintInvoicePdf,
  postApiBillingSendInvoicePdf,
  getApiBillingGetStatementLastSentInfo,
  getApiClaimGetClaimByIdClaimId,
  postApiClaimGetClaimLogByIdClaimId,
  postApiBillingGenerateBulkPatientStatement,
  getApiReportDownloadReport,
  getApiBillingDownloadBulkPatientStatement,
  postApiClaimMarkClaimStatus,
  postApiBillingSendBillForOnlinePayment,
  getApiEncounterGetAppointmentClaimsSummary,
  postApiTagUpdateClaimWiseTags,
  getApiTagGetClaimWiseTagsClaimId,
  postApiBillingModifyEOBWithInsurancePayment,
  postApiBillingCreateEOBWithInsurancePayment,
  deleteApiBillingDeleteEOB,
  getApiClaimGetAccidentDatesByPatientIdPatientId,
  postApiBillingShiftToPatientEobIdClaimId,
  postApiClaimGenerateBulkPaperClaims,
  getApiClaimDownloadBulkPaperClaims,
  postApiClaimResolveBillingProviderIdFromRules,
  postApiClaimSetClaimInsurance,
  postApiClaimSetClaimNotes,
  getApiSettingsGetEOBFilterSettings,
  getApiClaimGetSummaryForEncounterIdEncounterId,
  getApiClaimGetEOBSummariesByEncounterIdEncounterId,
  getApiSettingsGetPatientPaymentTransactionFilterSettings,
  postApiBillingDeletePatientPayment,
  postApiBillingPrintFinalLetterPdf,
  postApiBillingSendFinalLetterPdf,
  postApiBillingToggleExcludeServiceLineEobServiceCodePaymentInfoIdExclude,
  postApiMultimediaPostBillingMultiMedia,
  getApiSettingsGetClaimSettings,
  postApiClaimCreateClaimEncounterId,
  postApiClaimSaveClaim,
  getApiClaimGetSplitByEncounterIdEncounterId,
  postApiClaimSaveSplit,
  postApiClaimCreateSecondaryClaim,
  postApiClaimTransmitClaimToClearinghouseClaimId,
  deleteApiClaimDeleteClaimByIdClaimId,
  postApiClaimMarkClaimStatusBulk,
  postApiChargeGetClaimSummaryByClaimIds,
  postApiBillingAddOrUpdateEncounterPatientWriteOffs,
  postApiBillingMarkInsuranceWorkflowResolved,
  postApiEncounterGetEncounterPatientWriteOffsByEncounterId,
} from 'endpoints';
import {
  isNoAccessError,
} from 'utils/errors';
import {
  normalizeFilterableSettings,
} from 'core/Filters/util';
import {
  ReasonCode,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EOBAdjustmentCodePopover';
import {
  isEmptyString,
  isNilOrZero,
} from '@populateui/utils';
import {
  BulkPatientStatementTriggerResponseDTO,
} from 'dtos/bulkPatientStatementTriggerResponseDTO';
import {
  FileResultDTO,
} from 'dtos/fileResultDTO';
import {
  OnDrop,
} from 'pages/Dashboard/components/Dropzone';
import {
  TagDTO,
} from 'dtos/tagDTO';
import uniq from 'lodash/uniq';
import {
  SetClaimNotesDTO,
} from 'dtos/setClaimNotesDTO';
import {
  SetClaimInsuranceDTO,
} from 'dtos/setClaimInsuranceDTO';
import {
  PaymentFilterQuery,
} from 'pages/Dashboard/pages/Billing/Payments';
import {
  useFormContext,
} from 'react-hook-form';
import isEqual from 'lodash/isEqual';
import forIn from 'lodash/forIn';
import pick from 'lodash/pick';
import {
  visitNoteWithoutBillFields,
  appendVisitNoteTextField,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/utils/helper';
import uniqBy from 'lodash/uniqBy';
import {
  useSyncDateRange,
} from 'pages/Dashboard/hooks/url';
import {
  CreateEOBWithInsurancePaymentRequestDTO,
} from 'dtos/createEOBWithInsurancePaymentRequestDTO';

const transportFunctionsByType = Object.freeze({
  cpt: {
    create: postApiChargeCreateChargePolicy,
    update: postApiChargeModifyChargePolicy,
    delete: deleteApiChargeDeleteChargePolicy,
  },
  custom: {
    create: postApiChargeCreateCustomCodeChargePolicy,
    update: postApiChargeModifyCustomCodeChargePolicy,
    delete: deleteApiChargeDeleteCustomCodeChargePolicy,
  },
});

export function useUpsertServices():
  UseMutationResult<ChargeMasterDTO | CustomChargeMasterDTO, Error, Price> {
  return useMutation<ChargeMasterDTO | CustomChargeMasterDTO, Error, Price>({
    mutationFn: (data: Price) => {
      const { isCustom, id } = data;
      const { create, update } = transportFunctionsByType[isCustom ? 'custom' : 'cpt'];
      const serialized = serializePrice(data);
      return isNil(id) ? create(serialized) : update(serialized);
    },
  });
}

export function useDeleteChargePolicy(): UseMutationResult<
  ChargeMasterDTO | CustomChargeMasterDTO,
  Error,
  Partial<Price>> {
  return useMutation<
    ChargeMasterDTO | CustomChargeMasterDTO,
    Error,
    Partial<Price>>({
      mutationFn: async ({ id, isCustom }) => {
        const deleteChargePolicy = isCustom
          ? deleteApiChargeDeleteCustomCodeChargePolicy
          : deleteApiChargeDeleteChargePolicy;
        const response = await deleteChargePolicy({ id });
        return response;
      },
    });
}

export function useUpsertEOB():
  UseMutationResult<
   EOBWithInsurancePaymentResponseDTO,
   Error,
   CreateEOBWithInsurancePaymentRequestDTO
   > {
  return useMutation<
   EOBWithInsurancePaymentResponseDTO,
   Error,
   CreateEOBWithInsurancePaymentRequestDTO>({
     mutationFn: (data: EOBWithInsurancePaymentRequestDTO) => {
       const serialized = serializeEOBWithInsurancePaymentRequest(data);
       return isNil(data.eob?.eobId) || data.eob?.eobId === 0
         ? postApiBillingCreateEOBWithInsurancePayment(serialized)
         : postApiBillingModifyEOBWithInsurancePayment(serialized);
     },
   });
}

/// @TODO: replace with general print hook
export function usePrintPayment():
  UseMutationResult<MultiMediaResponse, Error, PatientInvoiceRequestDTO> {
  return useMutation<
    MultiMediaResponse, Error, PatientInvoiceRequestDTO
   >({ mutationFn: (data) => postApiBillingPrintInvoicePdf(data) });
}

export function useSendPatientReceipt():
  UseMutationResult<None, Error, PatientInvoiceRequestDTO> {
  return useMutation<None, Error, PatientInvoiceRequestDTO>({
    mutationFn: (data) => postApiBillingSendInvoicePdf(data),
  });
}

export function useGetInvoiceCommuniqueInfo(
  patientId: number,
  enabled = true,
): UseQueryResult<PatientStatementReminderResponseDTO | void, Error> {
  return useQuery<PatientStatementReminderResponseDTO | void, Error>({
    queryKey: ['patientInvoiceData', patientId],
    queryFn: () => getApiBillingGetStatementLastSentInfo({ patientId }),
    enabled: enabled && patientId > 0,
  });
}

export function useDeleteEOB(): UseMutationResult<None, Error, number> {
  return useMutation<None, Error, number>({
    mutationFn: (id: number) => deleteApiBillingDeleteEOB({ id }),
  });
}

export function useGetEOBDataForBilling(
  id: number,
  dateRange: DateRange,
): UseQueryResult<
  EOBDataForClaimViewDTO[], Error> {
  const serializedDateRange = serializeDateRange(dateRange);
  return useQuery<EOBDataForClaimViewDTO[], Error>({
    queryKey: ['eob-claims-data', id, serializedDateRange?.start, serializedDateRange?.end],
    queryFn: ({ signal }) => getApiBillingGetEOBByPatientPatientId(
      id,
      {
        startDate: serializedDateRange?.start,
        endDate: serializedDateRange?.end,
      },
      { signal },
    ),
    enabled: id > 0,
  });
}

export function useUploadBillingMedia(): UseMutationResult<
  BillingMultiMediaResponseDTO,
  Error,
  BillingMultiMediaUploadDTO> {
  return useMutation<
    BillingMultiMediaResponseDTO,
    Error,
    BillingMultiMediaUploadDTO>({
      mutationFn: (data) => postApiMultimediaPostBillingMultiMedia(data),
    });
}

export function useGetBillingMediaByCategories(
  id: number,
  multimediaTypes: MultimediaType[],
  generateUrls = true,
): UseQueryResult<MultiMediaResponse[], Error> {
  return useQuery<MultiMediaResponse[], Error>({
    queryKey: ['multimedia', id, ...multimediaTypes],
    queryFn: () => getMultiMedia({
      generateUrls,
      list: multimediaTypes.map((multimediaType) => ({
        originatorType: MediaOriginatorType.Billing,
        originatorId: id,
        multimediaType,
      })),
    }),
    enabled: id > 0 && multimediaTypes.length > 0,
  });
}

export function useCreateClaim(): UseMutationResult<ClaimBundleDTO, Error, number> {
  return useMutation<ClaimBundleDTO, Error, number>({
    mutationFn: (encounterId) => postApiClaimCreateClaimEncounterId(encounterId),
  });
}

export function useClaimSettings(): ClaimSettingsDTO {
  const { data } = usePrefetchedData<ClaimSettingsDTO>({
    key: ['claim-settings'],
    fetchFn: getApiSettingsGetClaimSettings,
  });

  return data ?? {};
}

export function useDeleteClaim(): UseMutationResult<None, Error, number> {
  return useMutation<None, Error, number>({
    mutationFn: (claimId) => deleteApiClaimDeleteClaimByIdClaimId(claimId),
  });
}

export function useGetClaimById(id: number = 0):
  UseQueryResult<ClaimBundleDTO, Error> {
  return useQuery<ClaimBundleDTO, Error>({
    queryKey: ['claim', id],
    queryFn: ({ signal }) => getApiClaimGetClaimByIdClaimId(id, { signal }),
    enabled: id > 0,
  });
}

export function useGetClaimLogById(id: number = 0):
  UseQueryResult<ClaimLogViewDTO, Error> {
  return useQuery<ClaimLogViewDTO, Error>({
    queryKey: ['claim', 'log', id],
    queryFn: async () => {
      const response = await postApiClaimGetClaimLogByIdClaimId(id);
      return {
        ...response,
        entries: [...(response.entries ?? [])].reverse(),
      };
    },
    enabled: id > 0,
  });
}

export function useGetSummaryForEncounterId(
  encounterId: number = 0,
): UseQueryResult<ChargeSummaryBundleDTO, Error> {
  return useQuery<ChargeSummaryBundleDTO, Error>({
    queryKey: ['charge-overview', 'encounter', encounterId],
    queryFn: ({ signal }) => getApiClaimGetSummaryForEncounterIdEncounterId(encounterId, { signal }),
    enabled: encounterId > 0,
    throwOnError: isNoAccessError,
  });
}

export function useGetEOBSummariesByEncounterId(
  encounterId: number = 0,
): UseQueryResult<EOBSummariesDTO, Error> {
  return useQuery<EOBSummariesDTO, Error>({
    queryKey: ['eob-summaries', 'encounter', encounterId],
    queryFn: ({ signal }) => getApiClaimGetEOBSummariesByEncounterIdEncounterId(encounterId, { signal }),
    enabled: encounterId > 0,
  });
}

export function useGetClaimSplitsByEncounter(
  id: number = 0,
): UseQueryResult<ClaimSplitsBundle, Error> {
  return useQuery<ClaimSplitsBundle, Error>({
    queryKey: ['claim', 'splits', id],
    queryFn: async () => {
      const response = await getApiClaimGetSplitByEncounterIdEncounterId(id);
      return {
        ...response,
        splitClaims: normalizeClaimSplits(response),
      };
    },
    enabled: id > 0,
  });
}

export function useSaveClaim(): UseMutationResult<ClaimBundleDTO, Error, ClaimBundleDTO> {
  return useMutation<ClaimBundleDTO, Error, ClaimBundleDTO>({
    mutationFn: (data) => postApiClaimSaveClaim(data),
  });
}

export function useMarkClaimStatus(): UseMutationResult<ClaimBundleDTO, Error, MarkClaimStatusDTO> {
  return useMutation<ClaimBundleDTO, Error, MarkClaimStatusDTO>({
    mutationFn: (data) => postApiClaimMarkClaimStatus(data),
  });
}

export function useGetClaimSummaryByClaimIds(
  claimIds: number[],
): UseQueryResult<LeanClaimSummaryDTO[], Error> {
  return useQuery<LeanClaimSummaryDTO[], Error>({
    queryKey: ['claim-summary-by-claim-ids', claimIds],
    queryFn: () => postApiChargeGetClaimSummaryByClaimIds({ claimIds }),
  });
}

export function useMarkClaimStatusBulk(): UseMutationResult<
    MarkClaimStatusBulkResponseDTO,
    Error,
    MarkClaimStatusBulkDTO
    > {
  return useMutation<
  MarkClaimStatusBulkResponseDTO,
  Error,
  MarkClaimStatusBulkDTO
  >({ mutationFn: (data) => postApiClaimMarkClaimStatusBulk(data) });
}

export function useSaveSplitClaim(): UseMutationResult<None, Error, SplitClaimsDTO> {
  return useMutation<None, Error, SplitClaimsDTO>({
    mutationFn: (data) => postApiClaimSaveSplit(data),
  });
}

export function useCreateSecondaryClaim(): UseMutationResult<
  ClaimBundleDTO, Error, CreateSecondaryClaimDTO> {
  return useMutation<ClaimBundleDTO, Error, CreateSecondaryClaimDTO>({
    mutationFn: (data) => postApiClaimCreateSecondaryClaim(data),
  });
}

export function useSaveClaimNotes(): UseMutationResult<
  ClaimBundleDTO, Error, SetClaimNotesDTO> {
  return useMutation<ClaimBundleDTO, Error, SetClaimNotesDTO>({
    mutationFn: (data) => postApiClaimSetClaimNotes(data),
  });
}

export function useSetClaimInsurance(): UseMutationResult<
  None, Error, SetClaimInsuranceDTO> {
  return useMutation<None, Error, SetClaimInsuranceDTO>({
    mutationFn: (data) => postApiClaimSetClaimInsurance(data),
  });
}

export function useClaimRequest(): UseMutationResult<ClaimBundleDTO, Error, number> {
  return useMutation<ClaimBundleDTO, Error, number>({
    mutationFn: (claimId) => postApiClaimTransmitClaimToClearinghouseClaimId(claimId),
  });
}

export function useGetClaimTags(claimId: number = 0):
  UseQueryResult<TagDTO[], Error> {
  return useQuery<TagDTO[], Error>({
    queryKey: ['claim-wise-tags', claimId],
    queryFn: () => getApiTagGetClaimWiseTagsClaimId(claimId),
    enabled: claimId > 0,
  });
}

export function useUpdateClaimWiseTags():
  UseMutationResult<TagDTO, Error, ClaimWiseTagRequestDTO> {
  return useMutation<TagDTO, Error, ClaimWiseTagRequestDTO>({
    mutationFn: (data) => postApiTagUpdateClaimWiseTags(data),
  });
}

export function useGetAccidentDatesByPatientId(
  patientId: number,
  enabled = true,
): UseQueryResult<string[], Error> {
  return useQuery<string[], Error>({
    queryKey: ['accident-dates', patientId],
    queryFn: async () => {
      const response = await getApiClaimGetAccidentDatesByPatientIdPatientId(patientId);
      return uniq(response ?? []);
    },
    enabled,
  });
}

export function usePatientInvoiceData(
  data: PatientStatementRequestDTO,
): UseQueryResult<PatientInvoice, Error> {
  return useQuery<PatientInvoice, Error>({
    queryKey: ['patientInvoiceData', data],
    queryFn: async () => {
      const response = await postApiBillingGetPatientStatement(data);
      return normalizePatientInvoice(response);
    },
  });
}

export function usePrintPatientStatement():
  UseMutationResult<MultiMediaResponseDTO, Error, PatientStatementRequest> {
  return useMutation<MultiMediaResponseDTO, Error, PatientStatementRequest>({
    mutationFn: (data: PatientStatementRequest) => {
      const serialized = serializePatientStatementPayload(data);
      return postApiBillingPrintStatementPdf(serialized);
    },
  });
}

export function usePrintPatientFinalNotice():
  UseMutationResult<MultiMediaResponseDTO, Error, PatientFinalLetterRequestDTO> {
  return useMutation<MultiMediaResponseDTO, Error, PatientFinalLetterRequestDTO>({
    mutationFn: (data) => postApiBillingPrintFinalLetterPdf(data),
  });
}

export function useSendBillForOnlinePayment():
  UseMutationResult<CommunicationResult, Error, OnlinePaymentRequestDTO> {
  return useMutation<
    CommunicationResult,
    Error,
    OnlinePaymentRequestDTO
  >({ mutationFn: (data) => postApiBillingSendBillForOnlinePayment(data) });
}

export function useSendPatientStatement():
  UseMutationResult<None, Error, PatientStatementRequest> {
  return useMutation<None, Error, PatientStatementRequest>({
    mutationFn: (data: PatientStatementRequest) => {
      const serialized = serializePatientStatementPayload(data);
      return postApiBillingSendStatementPdf(serialized);
    },
  });
}

export function useSendPatientFinalNotice():
  UseMutationResult<None, Error, PatientFinalLetterRequestDTO> {
  return useMutation<None, Error, PatientFinalLetterRequestDTO>({
    mutationFn: (data) => postApiBillingSendFinalLetterPdf(data),
  });
}

export function useUpdateWriteOff(): UseMutationResult<
None, Error, EncounterPatientWriteOffRequestDTO> {
  return useMutation<None, Error, EncounterPatientWriteOffRequestDTO>({
    mutationFn: (data) => postApiBillingAddOrUpdateEncounterPatientWriteOffs(data),
  });
}

export function useMarkInsuranceWorkflowResolved():
  UseMutationResult<InsuranceWorkflowResolutionDTO, Error, CreateInsuranceWorkflowResolutionDTO> {
  return useMutation<InsuranceWorkflowResolutionDTO, Error, CreateInsuranceWorkflowResolutionDTO>({
    mutationFn: (data) => postApiBillingMarkInsuranceWorkflowResolved(data),
  });
}

/// @TODO: ERROR is suppose to be properly handled by ReactQueryProvider
export function useNotifyPaymentUpdate() {
  const notify = useNotify();
  return (isComplete?: boolean, isCancelled?: boolean) => {
    const alertVariant = isComplete
      ? 'success' : 'error';

    const infoVariant = isCancelled ? 'info' : null;

    notify(paymentNotificationsByVariant[isNil(infoVariant) ? alertVariant : infoVariant]);
  };
}

export function useSearchAdjustmentReasonCode(
  searchString: string,
  enabled = true,
): UseQueryResult<ReasonCode[], Error> {
  return useQuery<ReasonCode[], Error>({
    queryKey: ['adjustment-reason-codes', searchString],
    queryFn: async () => {
      const response = await getApiClaimSearchAdjustmentReasonCode({ searchString });
      return ((response ?? []) as ClaimAdjustmentReasonCodeDTO[])
        .map(({ description, reasonCode }) => ({
          code: reasonCode ?? undefined,
          description,
        }));
    },
    enabled,
  });
}

export function useSearchRemittanceAdviceRemarkCodes(
  searchString: string,
  enabled = true,
): UseQueryResult<RemittanceAdviceRemarkCodeDTO[], Error> {
  return useQuery<RemittanceAdviceRemarkCodeDTO[], Error>({
    queryKey: ['remittance-advice-remark-codes', searchString],
    queryFn: async () => (
      await getApiClaimSearchRemittanceAdviceRemarkCodes(
        { searchString },
      )) as RemittanceAdviceRemarkCodeDTO[],
    enabled,
  });
}

export function useGetPatientPaymentFilterSettings() {
  const { data } = usePrefetchedData<SettingsDTO>({
    key: ['patient-payment-transaction-filter-settings'],
    fetchFn: getApiSettingsGetPatientPaymentTransactionFilterSettings,
  });

  return normalizeFilterableSettings(data?.filterableSettings ?? []);
}

export function useWriteOffOptions(): string[] {
  const { data } = usePrefetchedData<SettingsDTO>({
    key: ['payment-metadata'],
    fetchFn: getApiSettingsGetWriteOffSettings,
  });

  return compact(map(data?.writeOffSettings ?? [], 'reason'));
}

export function useGetEOBByNumber(
  eobNumber?: string | null,
): UseQueryResult<EOBWithInsurancePaymentResponseDTO, Error> {
  return useQuery({
    queryKey: ['eob', eobNumber],
    queryFn: ({ signal }) => {
      const response = getApiBillingGetEOB(
        { eobNumber: eobNumber ?? undefined },
        { signal },
      );
      return response as EOBWithInsurancePaymentResponseDTO;
    },
    enabled: !isEmptyString(eobNumber),
    placeholderData: keepPreviousData,
  });
}

export function usePrepareStatement(): UseMutationResult<
  BulkPatientStatementTriggerResponseDTO,
  Error,
  BulkPatientStatementGenerationDTO
  > {
  return useMutation<
    BulkPatientStatementTriggerResponseDTO,
    Error,
    BulkPatientStatementGenerationDTO
  >({ mutationFn: (data) => postApiBillingGenerateBulkPatientStatement(data) });
}

export function usePreparePaperClaims(): UseMutationResult<
  BulkPaperClaimsTriggerResponseDTO,
  Error,
  BulkPaperClaimsGenerationDTO
  > {
  return useMutation<
    BulkPaperClaimsTriggerResponseDTO,
    Error,
    BulkPaperClaimsGenerationDTO
  >({ mutationFn: (data) => postApiClaimGenerateBulkPaperClaims(data) });
}

const downloadTypes = ['report', 'statement', 'bulk paper claims'] as const;
type BulkDownload = typeof downloadTypes[number];

const downloadHandlerByType: Record<
  BulkDownload,
  [(...params: any[]) => Promise<FileResultDTO>, string]> = {
    report: [getApiReportDownloadReport, 'reportId'],
    statement: [getApiBillingDownloadBulkPatientStatement, 'bulkPatientStatementId'],
    'bulk paper claims': [getApiClaimDownloadBulkPaperClaims, 'bulkPaperClaimsId'],
  };

export function useDownloadFile(
  id: number,
  downloadId: string | null,
  type: string = 'report',
) {
  return useQuery<FileResultDTO, Error>({
    queryKey: [`${type}-download`, id, downloadId],
    queryFn: () => {
      const [getApi, keyId] = downloadHandlerByType[type as BulkDownload];
      return getApi({ [keyId]: id });
    },
    enabled: !isNil(downloadId),
  });
}

export const useFileDropzone = (
  onDrop?: OnDrop,
  accept?: Accept,
  multiple: boolean = false,
  disabled: boolean = false,
) => {
  const [dragItemsCount, setDragItemsCount] = useState<number>(0);
  const wrappedOnDrop: OnDrop | undefined = isNil(onDrop)
    ? undefined
    : (acceptedFiles, fileRejections, event) => {
      if (typeof document !== 'undefined' && document?.activeElement) {
        try {
          (document?.activeElement as HTMLElement)?.blur?.();
        } catch (_) {
          // no-op
        }
      }
      setDragItemsCount(0);
      onDrop?.(acceptedFiles, fileRejections, event);
    };
  const {
    getInputProps,
    getRootProps,
    isDragActive,
    isDragReject,
    isDragAccept,
    open,
  } = useDropzone({
    disabled,
    accept: isNil(accept)
      ? {
        'image/*': ['.png', '.jpg', '.jpeg', '.tiff'],
        'application/pdf': ['.pdf'],
      }
      : accept,
    multiple,
    ...(multiple ? {} : { maxFiles: 1 }),
    onDrop: wrappedOnDrop,
    onDragEnter: (event) => {
      const count = event?.dataTransfer?.items?.length ?? 0;
      setDragItemsCount(count);
    },
    onDragLeave: () => {
      setDragItemsCount(0);
    },
  });

  return {
    getInputProps,
    getRootProps,
    isDragActive,
    isDragReject,
    isDragAccept,
    isMultipleDrag: dragItemsCount > 1,
    open,
  };
};

export function useGetWriteOffs(encounterId: number, enabled: boolean = true) {
  return useQuery<EncounterPatientWriteOffDTO[], Error>({
    queryKey: ['write-offs', encounterId],
    queryFn: () => postApiEncounterGetEncounterPatientWriteOffsByEncounterId({ encounterId }),
    enabled: !isNilOrZero(encounterId) && enabled,
  });
}

export function useGetAppointmentClaimsSummary(encounterId: number, enabled: boolean = true) {
  return useQuery<AppointmentClaimsDTO, Error>({
    queryKey: ['appointment-claims-summary', encounterId],
    queryFn: () => getApiEncounterGetAppointmentClaimsSummary({ encounterId }),
    enabled: encounterId > 0 && enabled,
  });
}

export function useDeletePayment(): UseMutationResult<
  DeletePatientPaymentResponseDTO,
  Error,
  DeleteRequestDTO> {
  return useMutation<DeletePatientPaymentResponseDTO, Error, DeleteRequestDTO>({
    mutationFn: (data) => postApiBillingDeletePatientPayment(data),
  });
}

export function useShiftToPatient(): UseMutationResult<None, Error, EOBClaimRequest> {
  return useMutation<None, Error, EOBClaimRequest>({
    mutationFn: ({ eobId, claimId }) => postApiBillingShiftToPatientEobIdClaimId(eobId, claimId),
  });
}

export function useExcludeServiceLine(): UseMutationResult<None, Error, ExcludeServiceLineRequest> {
  return useMutation<None, Error, ExcludeServiceLineRequest>({
    mutationFn: ({
      eobServiceCodePaymentInfoId,
      isExcluded,
    }) => postApiBillingToggleExcludeServiceLineEobServiceCodePaymentInfoIdExclude(
      eobServiceCodePaymentInfoId,
      isExcluded,
    ),
  });
}

export function useResolveBillingProviderIdFromRules():
  UseMutationResult< BillingProviderRulesResponseDTO, Error, BillingProviderRulesRequestDTO> {
  return useMutation<BillingProviderRulesResponseDTO, Error, BillingProviderRulesRequestDTO>({
    mutationFn: (data) => postApiClaimResolveBillingProviderIdFromRules(data),
  });
}

export function useEOBFilterMetaComponents() {
  const { data } = usePrefetchedData<SettingsDTO>({
    key: ['eob-filter-settings'],
    fetchFn: getApiSettingsGetEOBFilterSettings,
  });

  return normalizeFilterableSettings(data?.filterableSettings ?? []);
}

export function usePaymentFilter(type: string) {
  const isEOBs = type === 'eobs';
  let dateRangeStorageKey = 'paymentsRange';

  if (type === 'refunds') {
    dateRangeStorageKey = 'refundsDateRange';
  } else if (isEOBs) {
    dateRangeStorageKey = 'eobsRange';
  }

  const eobFilter = useFilterQuery<PaymentFilterQuery>('EOBs', true);
  const patientPaymentFilter = useFilterQuery<PaymentFilterQuery>('patientPaymentsTransactions', true);
  const [dateRange, setDateRange] = useSyncDateRange({ uniqueKey: dateRangeStorageKey });

  const refundsFilter = {
    dateRange,
    setDateRange,
    filterQuery: {},
    filterInstanceRef: null,
    setFilterQuery: () => {},
  };

  const paymentFilter = isEOBs ? eobFilter : patientPaymentFilter;
  const activePaymentFilter = {
    ...paymentFilter,
    dateRange,
    setDateRange,
  };

  return type === 'refunds' ? refundsFilter : activePaymentFilter;
}

export function useAppendToVisitNote() {
  const { setValue, getValues } = useFormContext();

  const setFormValue = useCallback((value: string | BillingCodeDTO[], key: string) => {
    const defaultValue = Array.isArray(value) ? [] : '';
    const fieldValue = getValues(key) ?? defaultValue;
    const valueWithDefault = value ?? defaultValue;
    setValue(key, valueWithDefault, { shouldDirty: !isEqual(valueWithDefault, fieldValue) });
  }, [getValues, setValue]);

  const appendTextValue = useCallback((
    value: string | undefined,
    key: string,
    doubleLine: boolean = false,
  ) => {
    const currentValue = getValues(key);
    const newValue = appendVisitNoteTextField(currentValue, value, doubleLine);

    setFormValue(newValue, key);
  }, [getValues, setFormValue]);

  const appendToTextFields = useCallback((visitNote: VisitNoteDTO) => {
    forIn(pick(visitNote, visitNoteWithoutBillFields), (value, key) => {
      appendTextValue(value as string, key);
    });
  }, [appendTextValue]);

  const appendToBillFields = useCallback((bill: VisitNoteBillDTO) => {
    forIn(bill, (value, key) => {
      const newValue = compact([
        ...(getValues(`bill.${key}`) ?? []),
        ...(value ?? []) as BillingCodeDTO[],
      ]);
      setFormValue(key === 'serviceCodes'
        ? newValue
        : uniqBy(newValue, ({ code, serviceName }) => serviceName ?? code), `bill.${key}`);
    });
  }, [getValues, setFormValue]);

  const appendTextField = useCallback(
    (key: string, value: string) => appendTextValue(value, key),
    [appendTextValue],
  );

  const appendTextFieldWithDoubleLine = useCallback(
    (key: string, value: string) => appendTextValue(value, key, true),
    [appendTextValue],
  );
  return useMemo(() => ({
    appendToTextFields,
    appendToBillFields,
    appendTextField,
    appendTextFieldWithDoubleLine,
  }), [appendToTextFields, appendToBillFields, appendTextField, appendTextFieldWithDoubleLine]);
}
